import type {
  R2BundledAudioAsset,
  R2BundledAudioManifest,
} from "./r2AudioManifest";
import { validateR2AudioManifest } from "./r2AudioManifest";

export interface VoiceCallbacks {
  readonly onStart?: () => void;
  readonly onEnd?: () => void;
  readonly onUnavailable?: (cueId: string) => void;
  readonly onError?: (cueId: string) => void;
}

export type AudioPlaybackPort = Pick<
  HTMLAudioElement,
  "currentTime" | "onplaying" | "onended" | "onerror" | "pause" | "play"
>;

export type AudioPlaybackFactory = (asset: R2BundledAudioAsset) => AudioPlaybackPort;
export type BundledVoiceResult = "PLAYING" | "UNAVAILABLE" | "REJECTED_EXTERNAL_URL";

function browserAudioFactory(asset: R2BundledAudioAsset): AudioPlaybackPort {
  const audio = new Audio(asset.publicUrl);
  audio.preload = "auto";
  return audio;
}

export function createAnalysedAudioFactory(): {
  factory: AudioPlaybackFactory;
  getAnalyser: () => AnalyserNode | null;
} {
  let audioCtx: AudioContext | null = null;
  let analyser: AnalyserNode | null = null;
  const attachedElements = new WeakSet<HTMLAudioElement>();

  return {
    getAnalyser: () => analyser,
    factory: (asset: R2BundledAudioAsset) => {
      const audio = new Audio(asset.publicUrl);
      audio.preload = "auto";
      if (typeof window !== "undefined") {
        const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
        if (AudioCtx) {
          try {
            if (!audioCtx) {
              audioCtx = new AudioCtx();
              analyser = audioCtx.createAnalyser();
              analyser.fftSize = 256;
              analyser.smoothingTimeConstant = 0.45;
            }
            if (!attachedElements.has(audio) && analyser) {
              const source = audioCtx.createMediaElementSource(audio);
              source.connect(analyser);
              analyser.connect(audioCtx.destination);
              attachedElements.add(audio);
            }
            if (audioCtx.state === "suspended") {
              void audioCtx.resume();
            }
          } catch {
            // Non-fatal fallback to normal browser audio element
          }
        }
      }
      return audio;
    },
  };
}

export class BundledTeacherVoice {
  readonly #manifest: R2BundledAudioManifest;
  readonly #factory: AudioPlaybackFactory;
  readonly #baseUrl: string;
  #generation = 0;
  #activeAudio: AudioPlaybackPort | null = null;

  constructor(
    manifest: R2BundledAudioManifest,
    factory: AudioPlaybackFactory = browserAudioFactory,
    baseUrl: string = window.location.href,
  ) {
    const issues = validateR2AudioManifest(manifest);
    if (issues.length > 0) {
      throw new Error(`R2 audio manifest rejected: ${issues.map(({ cueId, reason }) => `${cueId}:${reason}`).join(",")}`);
    }
    this.#manifest = manifest;
    this.#factory = factory;
    this.#baseUrl = baseUrl;
  }

  hasCue(cueId: string): boolean {
    return this.#manifest[cueId] !== undefined;
  }

  stop(): void {
    this.#generation += 1;
    if (this.#activeAudio === null) {
      return;
    }
    this.#activeAudio.onplaying = null;
    this.#activeAudio.onended = null;
    this.#activeAudio.onerror = null;
    this.#activeAudio.pause();
    this.#activeAudio.currentTime = 0;
    this.#activeAudio = null;
  }

  play(cueId: string, callbacks: VoiceCallbacks = {}): BundledVoiceResult {
    this.stop();
    const asset = this.#manifest[cueId];
    if (asset === undefined) {
      callbacks.onUnavailable?.(cueId);
      callbacks.onEnd?.();
      return "UNAVAILABLE";
    }

    const resolvedUrl = new URL(asset.publicUrl, this.#baseUrl);
    const allowedOrigin = new URL(this.#baseUrl).origin;
    if (resolvedUrl.origin !== allowedOrigin) {
      callbacks.onError?.(cueId);
      callbacks.onEnd?.();
      return "REJECTED_EXTERNAL_URL";
    }

    const generation = this.#generation;
    const audio = this.#factory(Object.freeze({ ...asset, publicUrl: resolvedUrl.href }));
    this.#activeAudio = audio;
    audio.onplaying = () => {
      if (generation === this.#generation) {
        callbacks.onStart?.();
      }
    };
    audio.onended = () => {
      if (generation === this.#generation) {
        this.#activeAudio = null;
        callbacks.onEnd?.();
      }
    };
    audio.onerror = () => {
      if (generation === this.#generation) {
        this.#activeAudio = null;
        callbacks.onError?.(cueId);
        callbacks.onEnd?.();
      }
    };
    void audio.play().catch(() => {
      if (generation === this.#generation) {
        this.#activeAudio = null;
        callbacks.onError?.(cueId);
        callbacks.onEnd?.();
      }
    });
    return "PLAYING";
  }
}
